import { useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { Target } from "lucide-react";
import { useReportFilters } from "../../contexts/ReportFiltersContext";
import { formatNumber } from "../../utils/format";
import EmptyState from "../../components/EmptyState";
import ReportSectionCard from "../../components/ReportSectionCard";
import PipelineFilters from "./components/PipelineFilters";
import PipelineFunnel from "./components/PipelineFunnel";
import PipelineTable from "./components/PipelineTable";
import PipelineNewContactsChart from "./components/PipelineNewContactsChart";
import PipelineEmptyState from "./components/PipelineEmptyState";
import { usePipelineReport } from "./hooks/usePipelineReport";

const PipelineReportPage = () => {
  const {
    shopId,
    from,
    to,
    isRangeValid,
    compareEnabled,
    compareFrom,
    compareTo,
  } = useReportFilters();
  const outletCtx = useOutletContext() || {};
  const { openStagesManager, stagesVersion = 0 } = outletCtx;

  const auth = useSelector((s) => s.auth);
  const accountId = auth?.user?.accountId;

  const { data, loading, error } = usePipelineReport({
    shopId,
    accountId,
    from,
    to,
    compareFrom,
    compareTo,
    compareEnabled,
    reloadKey: stagesVersion,
  });

  if (!shopId || !isRangeValid) {
    return (
      <div className="p-6">
        <EmptyState title="Chọn Zalo để xem báo cáo" />
      </div>
    );
  }

  const hasRealStages =
    data?.stages?.some((s) => s.id !== null) || false;

  if (!loading && data && !hasRealStages) {
    return (
      <div className="p-4 max-w-7xl">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Giai đoạn bán hàng
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Phân bố contacts theo pipeline, phân tích theo kỳ
          </p>
        </div>
        <PipelineEmptyState onCreateStage={openStagesManager} />
      </div>
    );
  }

  const meta = data?.meta;

  return (
    <div className="p-4 space-y-4 max-w-7xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Giai đoạn bán hàng
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Phân bố contacts theo pipeline trong khoảng đã chọn
          </p>
        </div>
        {meta && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">
              <Target size={11} /> {formatNumber(meta.withStage)} có giai đoạn
            </span>
            <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-600">
              {formatNumber(meta.withoutStage)} chưa phân loại
            </span>
            {meta.truncated && (
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-amber-50 text-amber-700">
                Kết quả đã cắt — hãy thu hẹp khoảng ngày
              </span>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded text-sm">{error}</div>
      )}

      <PipelineFilters />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <ReportSectionCard
          className="lg:col-span-5"
          title="Phễu giai đoạn"
          subtitle="Thanh rộng theo số contacts ở mỗi stage"
        >
          <PipelineFunnel stages={data?.stages || []} loading={loading} />
        </ReportSectionCard>

        <ReportSectionCard
          className="lg:col-span-7"
          title="Chi tiết theo giai đoạn"
          subtitle={compareEnabled ? "So với kỳ trước cùng độ dài" : undefined}
        >
          <PipelineTable
            stages={data?.stages || []}
            total={meta?.total || 0}
            comparison={compareEnabled}
            loading={loading}
          />
        </ReportSectionCard>
      </div>

      <ReportSectionCard
        title="Contacts mới vào pipeline"
        subtitle="Số contact mới theo ngày tạo contact"
      >
        <PipelineNewContactsChart
          series={data?.newContactsSeries || []}
          loading={loading}
        />
      </ReportSectionCard>

      <div className="pt-1 flex items-center justify-between text-xs text-gray-400">
        <span>
          Dữ liệu trong khoảng. Stage "Chưa phân loại" gom contacts chưa gán giai đoạn.
        </span>
        {openStagesManager && (
          <button
            type="button"
            onClick={openStagesManager}
            className="text-blue-600 hover:underline"
          >
            Quản lý giai đoạn
          </button>
        )}
      </div>
    </div>
  );
};

export default PipelineReportPage;
